// Зөвшөөрлийн хуудас (эцэг эх) — /consent/:id. Цаасан маягт шиг: нөхцөл → "ЗӨВШӨӨРСӨН:" хэсэгт асран хамгаалагч бүрийн
// мэдэгдэл, гарын үсэг, огноо (2 асран хамгаалагч зөвшөөрвөл 2 хэсэг). "Зөвшөөрөх" дарахад хүүхдээ сонгож гарын үсгээ зурна;
// нэр, сурагчийн ID, огноог бүртгэлээс бөглөнө.
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Modal, Spin } from "antd";
import { FiArrowLeft, FiClock } from "react-icons/fi";
import dayjs from "dayjs";
import { instance } from "utils/axios";
import { dueText } from "utils/due";
import SignaturePad from "./SignaturePad";

// Харьяалахын тийн ялгал (ойролцоо): Бат → Батын, Аръяа → Аръяагийн, Энхжин → Энхжиний
const genitive = (name) => {
  const w = String(name || "").trim();
  const low = w.toLowerCase();
  const back = /[аоуяёю]/.test(low) && !/[эөүе]/.test(low);
  const last = low.slice(-1);
  if (last === "и") return `${w}йн`;
  if (/[аэоөуүяеёюы]/.test(last)) return `${w}гийн`;
  if (last === "н") return `${w}${back ? "ы" : "ий"}`;
  return `${w}${back ? "ын" : "ийн"}`;
};
const fullName = (k) => [k.lastName, k.firstName].filter(Boolean).join(" ");
const day = (d) => dayjs(d).format("YYYY.MM.DD");

// Нэг асран хамгаалагчийн зөвшөөрөл — цаасан маягтын баганатай адил
function SignatureBlock({ child, sig }) {
  return (
    <div className="rounded-[14px] border border-[#e9e8f0] bg-white p-4 flex flex-col gap-3">
      <p className="m-0 text-[14px] leading-relaxed text-[#1c1c1c]">
        {child ? (
          <>
            <b>{child.code}</b> сурагчийн ID-тай <b>{genitive(fullName(child))}</b> эцэг/эх, асран хамгаалагч би <b>{sig.signerName}</b> нь
            өөрийн хүүхдийг дээрх дурдсан нөхцөлүүдийг уншиж, зөвшөөрч, хамрагдахдаа сургуулийн дотоод журам болон зохицуулалтыг дагаж мөрдөхийг зөвшөөрч байна.
          </>
        ) : (
          <>Би <b>{sig.signerName}</b> дээрх дурдсан нөхцөлүүдийг уншиж, зөвшөөрч байна.</>
        )}
      </p>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex flex-col">
          <span className="text-xs text-[#6e5e8a]">Гарын үсэг:</span>
          {sig.signatureImage ? <img src={sig.signatureImage} alt={`${sig.signerName}-ийн гарын үсэг`} className="h-14 w-auto max-w-[220px] border-b border-[#d9cdea]" />
            : <span className="h-14 w-[180px] border-b border-[#d9cdea]" />}
        </div>
        <div className="text-[13px] text-[#1c1c1c]"><span className="text-[#6e5e8a]">Огноо: </span>{day(sig.agreedAt)}</div>
      </div>
    </div>
  );
}

export default function ConsentPage() {
  const { id } = useParams();
  const [c, setC] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState([]);
  const [agree, setAgree] = useState(false);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    instance({ method: "get", url: `/guardian/consents/${id}` })
      .then((res) => setC(res.status === 204 ? null : res.data))
      .catch(() => setC(null));
  }, [id]);

  // Миний гарын үсэг зураагүй хүүхдүүд (нөгөө асран хамгаалагч зурсан ч би зурж болно)
  const pending = useMemo(() => (c?.perChild ? c.children.filter((k) => !k.mine) : c && !c.signed ? [null] : []), [c]);
  const blocks = c?.perChild ? c.children.flatMap((k) => k.signatures.map((s, i) => ({ key: `${k.id}-${i}`, child: k, sig: s }))) : c?.signed ? [{ key: "own", child: null, sig: c.signed }] : [];
  const picked = c?.perChild ? c.children.filter((k) => pick.includes(k.id)) : [];
  const canSign = agree && !!signature && !saving && (c?.perChild ? pick.length > 0 : true);

  const start = () => { setPick(pending.filter(Boolean).map((k) => k.id)); setAgree(false); setSignature(null); setOpen(true); };
  const sign = () => {
    setSaving(true);
    instance({ method: "post", url: `/guardian/consents/${id}/sign`, data: { agree: true, studentIds: pick, signatureImage: signature } })
      .then((res) => { setC(res.data); setOpen(false); })
      .catch(() => {})
      .then(() => setSaving(false));
  };

  return (
    <div className="pt-3.5 md:pt-7 pb-8 max-w-[820px] mx-auto flex flex-col gap-4">
      <Link to="/" className="self-start inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-[#3d1b6e] min-h-[44px]">
        <FiArrowLeft size={16} /> Нүүр
      </Link>
      {c === undefined ? (
        <div className="flex justify-center py-10"><Spin /></div>
      ) : !c ? (
        <section className="tj-card p-6 text-center text-sm text-[#6e5e8a]">Энэ зөвшөөрлийн хуудас олдсонгүй эсвэл хаагдсан байна.</section>
      ) : (
        <section className="tj-card p-4 md:p-8 flex flex-col gap-5">
          <div className="flex flex-col items-center text-center gap-1.5">
            <span className="text-[11px] md:text-xs font-bold tracking-[0.12em] uppercase text-[#0e7490]">Зөвшөөрөл{c.type ? ` · ${c.type}` : ""}</span>
            <h1 className="m-0 text-[20px] md:text-[26px] font-extrabold leading-snug uppercase tracking-[0.01em] text-primary">{c.title.replace(/^\[ТЕСТ\]\s*/, "")}</h1>
            {c.dueDate && !c.done && <span className="inline-flex items-center gap-1.5 text-sm text-[#6e5e8a]"><FiClock size={14} />{dueText(c.dueDate)}</span>}
          </div>

          {/* Нөхцөл */}
          <div className="text-[15px] leading-relaxed text-[#1c1c1c] whitespace-pre-line">{c.body}</div>

          {/* ЗӨВШӨӨРСӨН: асран хамгаалагч бүрийн хэсэг */}
          <div className="flex flex-col gap-3 border-t border-[#efedf4] pt-4">
            <div className="text-center text-sm font-extrabold tracking-[0.08em] text-[#1c1c1c]">ЗӨВШӨӨРСӨН:</div>
            {blocks.length ? (
              <div className="grid gap-3 md:grid-cols-2">
                {blocks.map((b) => <SignatureBlock key={b.key} child={b.child} sig={b.sig} />)}
              </div>
            ) : (
              <div className="text-center text-sm text-[#6e5e8a] py-2">Хараахан зөвшөөрөөгүй байна.</div>
            )}
            {pending.length > 0 && (
              <button onClick={start} className="mt-1 h-12 px-8 rounded-full bg-primary text-white text-[15px] font-bold hover:bg-[#4b0d76] self-center">
                Зөвшөөрөх
              </button>
            )}
          </div>
        </section>
      )}

      {/* Зөвшөөрөх: хүүхэд сонгох, мэдэгдэл, гарын үсэг */}
      <Modal open={open} onCancel={() => setOpen(false)} footer={null} centered width={520} title="Зөвшөөрөх" destroyOnClose>
        {c && (
          <div className="flex flex-col gap-4 pt-1">
            {c.perChild && pending.length > 1 && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-semibold">Аль хүүхдийн өмнөөс</span>
                {pending.map((k) => (
                  <label key={k.id} className="flex items-center gap-3 rounded-xl border border-[#e5d8f5] px-3.5 py-2.5 cursor-pointer">
                    <input type="checkbox" className="h-5 w-5 accent-[#5c1090]" checked={pick.includes(k.id)}
                      onChange={(e) => setPick((p) => (e.target.checked ? [...p, k.id] : p.filter((x) => x !== k.id)))} />
                    <span className="flex-1 text-[15px]">{fullName(k)} <span className="text-[#6e5e8a] text-sm">· {k.code}</span></span>
                  </label>
                ))}
              </div>
            )}
            <label className="flex items-start gap-3 cursor-pointer rounded-xl bg-[#faf7ff] p-3.5">
              <input type="checkbox" className="h-5 w-5 mt-0.5 accent-[#5c1090] shrink-0" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
              <span className="text-[14px] leading-relaxed text-[#1c1c1c]">
                {c.perChild ? (
                  <>
                    {picked.length ? picked.map((k, i) => <span key={k.id}>{i ? ", " : ""}<b>{k.code}</b> сурагчийн ID-тай <b>{genitive(fullName(k))}</b></span>) : "сонгосон хүүхдийн"} эцэг/эх,
                    асран хамгаалагч би <b>{c.signerName}</b> нь өөрийн хүүхдийг дээрх дурдсан нөхцөлүүдийг уншиж, зөвшөөрч, хамрагдахдаа сургуулийн
                    дотоод журам болон зохицуулалтыг дагаж мөрдөхийг зөвшөөрч байна.
                  </>
                ) : <>Би <b>{c.signerName}</b> дээрх дурдсан нөхцөлүүдийг уншиж, зөвшөөрч байна.</>}
              </span>
            </label>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold">Гарын үсэг</span>
                <span className="text-xs text-[#6e5e8a]">Огноо: {day(new Date())}</span>
              </div>
              <SignaturePad onChange={setSignature} />
            </div>
            <button onClick={sign} disabled={!canSign}
              className="h-12 rounded-full bg-primary text-white text-[15px] font-bold hover:bg-[#4b0d76] disabled:bg-[#d9cdea] disabled:cursor-not-allowed">
              {saving ? "Хадгалж байна…" : "Зөвшөөрч гарын үсэг зурах"}
            </button>
            <span className="text-xs text-[#6e5e8a] text-center">Гарын үсэг, огноо, цаг, төхөөрөмжийн мэдээлэл баримт болж хадгалагдана.</span>
          </div>
        )}
      </Modal>
    </div>
  );
}
